import { ColDef, ColumnState, EditableCallbackParams, ValueGetterParams } from 'ag-grid-community';
import React from 'react';
import { aggridHeaderClass, aggridSwitchButtonWrapperStyle } from 'src/components/common/common';
import { MenuDto } from 'src/dtos/menu/menuDto';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { CommonCodeColumn } from 'src/utils/grid/GridColumnContent';
import { GridDropDownCommitAfter, getSwitchButtonColumnOptions } from 'src/utils/grid/GridHandlerContent';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { showWarning } from 'src/utils/toast/toastUtil';

import { SYS2010_R01Dto } from './Dto_SYS2010_R01';

export const COLUMN_STATE_KEY = 'SYS2010_R01-grid-column-state';

export const DEFAULT_PINNED_COL_IDS: string[] = [];

export const headerNames: Record<string, string> = {
	rowIndex: '',
	menuCd: '메뉴코드',
	menuNm: '메뉴명',
	upMenu: '상위메뉴코드',
	useYn: '사용여부',
	displayOnly: '메뉴전용여부',
	menuLevel: '메뉴레벨',
	menuSeq: '메뉴순서',
	devloper: '개발자',
	queryAut: '조회권한',
	insertAut: '등록권한',
	deleteAut: '삭제권한',
	saveAut: '저장권한',
	printAut: '출력권한',
	excelAut: '엑셀권한',
	devSts: '개발상태',
	manualPage: '메뉴얼페이지',
	remark: '비고',
};

interface CommonCodeListItem {
	minorCd: string;
	codeNm: string;
}

export interface CreateBaseColumnDefsParams {
	indexCellRenderer: (params: SYS2010_R01Dto) => React.ReactNode;
	handleSwitchButtonHandler: (
		valueIndex: number,
		params: unknown,
		newValue: 'Y' | 'N',
		headerNames: Record<string, string>,
	) => void;
	menuCdList: MenuDto[];
	devloperList: CommonCodeListItem[];
	devStsList: CommonCodeListItem[];
	updateSYS2010_R01List: (valueIndex: number, data: SYS2010_R01Dto) => void;
	handleGridDropDownCommitAfter: GridDropDownCommitAfter<SYS2010_R01Dto>;
	defaultManualPage: number;
}

const menuCdEditable = (params: EditableCallbackParams<SYS2010_R01Dto>) =>
	params.data?.inserted === true;

export const createBaseColumnDefs = ({
	indexCellRenderer,
	handleSwitchButtonHandler,
	menuCdList,
	devloperList,
	devStsList,
	updateSYS2010_R01List,
	handleGridDropDownCommitAfter,
	defaultManualPage,
}: CreateBaseColumnDefsParams): ColDef<SYS2010_R01Dto>[] => [
	...getDefaultRowIndexColumnConfig(indexCellRenderer),
	{
		headerName: '메뉴코드', colId: 'menuCd', field: 'menuCd', width: 120,
		editable: menuCdEditable,
		pasteable: true,
	} as ColDef<SYS2010_R01Dto>,
	{
		headerName: '메뉴명', colId: 'menuNm', field: 'menuNm', width: 150,
		editable: true,
		pasteable: true,
	} as ColDef<SYS2010_R01Dto>,
	{
		headerName: '상위메뉴코드', colId: 'upMenu', field: 'upMenu', width: 100,
		editable: false,
		valueGetter: (params: ValueGetterParams<SYS2010_R01Dto>) =>
			menuCdList.find((value) => value.menuCd === params.data?.upMenu)?.menuNm || '',
		cellRenderer: (params: ValueGetterParams<SYS2010_R01Dto>) =>
			menuCdList.find((value) => value.menuCd === params.data?.upMenu)?.menuNm,
	},
	{
		headerName: '사용여부', colId: 'useYn', field: 'useYn', width: 75,
		...getSwitchButtonColumnOptions('useYn', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
	},
	{
		headerName: '메뉴전용여부', colId: 'displayOnly', field: 'displayOnly', width: 100,
		...getSwitchButtonColumnOptions('displayOnly', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
	},
	{
		headerName: '메뉴레벨', colId: 'menuLevel', field: 'menuLevel', width: 75,
		editable: true,
		cellEditor: 'agSelectCellEditor',
		cellEditorParams: { values: [1, 2, 3] },
	},
	{
		headerName: '메뉴순서', colId: 'menuSeq', field: 'menuSeq', width: 75,
		editable: true,
		pasteable: true,
		valueParser: (params) =>
			Number(String(params.newValue ?? '').replaceAll(',', '').trim()) || 0,
	} as ColDef<SYS2010_R01Dto>,
	{
		headerName: '개발자', colId: 'devloper', field: 'devloper', width: 70,
		...CommonCodeColumn.options(devloperList, true, CommonCodeColumn.change(updateSYS2010_R01List), handleGridDropDownCommitAfter),
	},
	{
		headerName: '조회권한', colId: 'queryAut', field: 'queryAut', width: 75,
		...getSwitchButtonColumnOptions('queryAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
	},
	{
		headerName: '등록권한', colId: 'insertAut', field: 'insertAut', width: 75,
		...getSwitchButtonColumnOptions('insertAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
	},
	{
		headerName: '삭제권한', colId: 'deleteAut', field: 'deleteAut', width: 75,
		...getSwitchButtonColumnOptions('deleteAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
	},
	{
		headerName: '저장권한', colId: 'saveAut', field: 'saveAut', width: 75,
		...getSwitchButtonColumnOptions('saveAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
	},
	{
		headerName: '출력권한', colId: 'printAut', field: 'printAut', width: 75,
		...getSwitchButtonColumnOptions('printAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
	},
	{
		headerName: '엑셀권한', colId: 'excelAut', field: 'excelAut', width: 75,
		...getSwitchButtonColumnOptions('excelAut', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
	},
	{
		headerName: '개발상태', colId: 'devSts', field: 'devSts', width: 75,
		...CommonCodeColumn.options(devStsList, true, CommonCodeColumn.change(updateSYS2010_R01List), handleGridDropDownCommitAfter),
	},
	{
		headerName: '메뉴얼페이지', colId: 'manualPage', field: 'manualPage', width: 110,
		editable: true,
		pasteable: true,
		valueParser: (params) => {
			const numericValue = Number(params.newValue);
			if (Number.isNaN(numericValue)) {
				showWarning('메뉴얼페이지는 숫자만 입력 가능합니다.');
				return params.oldValue ?? defaultManualPage;
			}
			if (numericValue < 1) {
				showWarning('메뉴얼페이지는 1 이상의 값이어야 합니다.');
				return 1;
			}
			return Math.trunc(numericValue);
		},
	} as ColDef<SYS2010_R01Dto>,
	{
		headerName: '비고', colId: 'remark', field: 'remark', width: 150,
		editable: true,
		pasteable: true,
	} as ColDef<SYS2010_R01Dto>,
];

export const createDefaultColumnState = (
	baseColumnDefs: ColDef<SYS2010_R01Dto>[],
) => buildDefaultColumnStateFromDefs(baseColumnDefs, DEFAULT_PINNED_COL_IDS);

export const createColumnDefs = (
	baseColumnDefs: ColDef<SYS2010_R01Dto>[],
	columnState: ColumnState[] | null,
): ColDef<SYS2010_R01Dto>[] => {
	if (columnState === null) return baseColumnDefs;
	return GridCustomColumnDefs<SYS2010_R01Dto>({
		baseColumnDefs,
		columnState,
		headerNames,
		fallbackHeaderClass: aggridHeaderClass,
	});
};
